<template>
  <div class="tw-flex tw-justify-between tw-gap-10 tw-text-[#1f2229]">
    <div />
    <div class="tw-flex tw-gap-10">
      <div class="tw-bg-white tw-p-12 tw-w-full">
        <AreaHeader />
        <div class="tw-p-10">
          <div>
            <h2 class="tw-font-georgia tw-font-bold tw-text-5xl">Executive Summary</h2>
            <AdvisorCoreStripes />
          </div>
          <div class="tw-font-poppins tw-mt-10">
            <p class="tw-mb-4">{{ introText }}</p>

               <!-- Reformatted "How to read this summary" section -->
            <div class="tw-mb-8 tw-border-t tw-border-b tw-border-gray-300 tw-py-4">
              <h3 class="tw-font-bold tw-text-lg tw-mb-2">How to Read This Summary</h3>

              <div class="columns">
                <div class="column is-half">
                  <p class="tw-mb-2">This executive summary provides a comparison between two portfolios:</p>
                  <ul class="tw-list-disc tw-list-inside tw-mb-2">
                    <li><strong>Current Portfolio:</strong> Your current investment portfolio (accountStatement.1)</li>
                    <li><strong>Proposed Portfolio:</strong> The proposed or benchmark portfolio (SPY)</li>
                  </ul>
                </div>
                <div class="column is-half">
                  <p class="tw-mb-2">Risk Suitability Information:</p>
                  <ul class="tw-list-none tw-pl-4">
                    <li><strong>Your Risk Profile:</strong> Moderately Aggressive (Score: {{ clientRiskScore }})</li>
                    <li><strong>Current Portfolio Risk Score:</strong> {{ startingRiskScore }}</li>
                    <li><strong>Proposed Portfolio Risk Score:</strong> {{ destinationRiskScore }}</li>
                  </ul>
                </div>
              </div>
              <p class="tw-mt-2">The following table interprets how each aspect of the portfolio change may move you towards or away from your target risk score. This analysis aims to help you understand the potential impact of the proposed changes in the context of your risk tolerance.</p>
            </div>
            
            <table class="tw-w-full tw-border-collapse tw-mt-6">
              <thead>
                <tr class="tw-bg-[#00345d] tw-text-white">
                  <th class="tw-p-2 tw-text-left"></th>
                  <th class="tw-p-2 tw-text-left">Current</th>
                  <th class="tw-p-2 tw-text-left">Recommendation</th>
                  <th class="tw-p-2 tw-text-left">Improvement?</th>
                  <th class="tw-p-2 tw-text-left">Summary note</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in summaryItems" :key="index" :class="index % 2 === 0 ? 'tw-bg-gray-100' : 'tw-bg-white'">
                  <td class="tw-p-2 tw-font-semibold">{{ item.analysisType }}</td>
                  <td class="tw-p-2">{{ item.startingValue }}</td>
                  <td class="tw-p-2">{{ item.destinationValue }}</td>
                  <td class="tw-p-2" :class="getRiskAlignmentClass(item.riskAlignment)">
                    {{ item.riskAlignment }}
                  </td>
                  <td class="tw-p-2">{{ item.interpretation }}</td>
                </tr>
              </tbody>
            </table>
            <br>
            <p class="tw-mb-4">
              <strong>Methodology</strong>
              <br>
              For each category, we assess whether the current portfolio characteristics are likely to increase or decrease overall portfolio risk relative to your target risk level. "Moving closer" indicates the characteristic is bringing the portfolio's risk closer to your target, while "Moving away" suggests it's taking the risk level further from your target. "Needs review" is used when the impact is mixed or unclear.
            </p>
            <p>
              It's important to note that this analysis provides a general assessment based on widely accepted principles. Individual circumstances, market conditions, and the interplay between different portfolio elements can all affect actual risk and returns. This summary should serve as a starting point for discussion with your financial advisor.
            </p>
          </div>
          <div class="tw-mt-8 tw-text-sm tw-text-gray-600">{{ footerText }}</div>
        </div>
        <AreaFooter />
      </div>
      <AreaSidebar
        :area="area"
      >
        <IncludeInPresentationToggle :name="area?.name" />
        <template #other>
          <SpeakersNotes :notes="area?.explanation" />
          <!-- <AIChat /> -->
        </template>
      </AreaSidebar>
    </div>
    <div />
  </div>
</template>

<script>
import AdvisorCoreStripes from '@frontend/ui/features/checkup/components/AdvisorCoreStripes.vue';
import AreaHeader from '@frontend/ui/features/checkup/components/analytics/shared/AreaHeader.vue';
import AreaFooter from '@frontend/ui/features/checkup/components/analytics/shared/AreaFooter.vue';
import AreaSidebar from '@frontend/ui/features/checkup/components/sidebar/AreaSidebar.vue';
import IncludeInPresentationToggle from '@frontend/ui/features/checkup/components/sidebar/IncludeInPresentationToggle.vue';
import SpeakersNotes from '@frontend/ui/features/checkup/components/sidebar/SpeakersNotes.vue';
import AIChat from '@frontend/ui/features/checkup/components/sidebar/AIChat.vue';

export default {
  name: 'ExecutiveSummaryArea',
  components: {
    AdvisorCoreStripes,
    AreaHeader,
    AreaFooter,
    AreaSidebar,
    IncludeInPresentationToggle,
    SpeakersNotes,
    AIChat,
  },
  props: {
    area: Object,
  },
  data() {
    return {
      clientRiskScore: 75,
      startingRiskScore: 16.43,
      destinationRiskScore: 90.05,
      summaryItems: [
        {
          analysisType: 'Risk',
          startingValue: 'Conservative',
          destinationValue: 'Aggressive',
          riskAlignment: 'Opportunity for Improvement',
          interpretation: `Based on your risk tolerance questionnaire, you should invest in a risky portfolio to fulfil your goal of long term wealth maximization.`
        },
        {
          analysisType: 'Asset Allocation',
          startingValue: '33% Stocks, 65% Bonds, 2% Cash',
          destinationValue: '95% Stocks, 3% Bonds, 2% Cash',
          riskAlignment: 'Opportunity for Improvement',
          interpretation: `Because of your long investment time horizon and goal of building long-term wealth, you should invest in a more stock-heavy portfolio.`
        },
        {
          analysisType: 'Asset Location',
          startingValue: 'Large cash position in Roth IRA',
          destinationValue: 'High growth assets in Roth, bonds in 401k',
          riskAlignment: 'Opportunity for Improvement',
          interpretation: `By placing your high growth assets in a Roth IRA, and corporate bonds in your 401k, we will maximize your tax savings at retirement.`
        },
        {
          analysisType: 'Expense Ratio',
          startingValue: '1.45%',
          destinationValue: '0.09%',
          riskAlignment: 'Opportunity for Improvement',
          interpretation: `A lower expense ratio means you pay less in fees.`
        },
        {
          analysisType: 'Diversification (Style)',
          startingValue: '100% Value',
          destinationValue: '15% Value, 20% Core, 65% Growth',
          riskAlignment: 'Opportunity for Improvement',
          interpretation: `Diversifying styles lowers portfolio risk. A higher focus on growth investments aligns with your aggressive risk goals.`
        },
        {
          analysisType: 'Diversification (Market Capitalization)',
          startingValue: '100% Large Cap',
          destinationValue: '75% Large Cap, 20% Mid Cap, 5% Large Cap',
          riskAlignment: `Opportunity for Improvement`,
          interpretation: `A larger small and mid cap exposure better aligns with your aggressive risk goals.`
        },
        {
          analysisType: 'Diversification (Geography)',
          startingValue: '100% US',
          destinationValue: '70% US, 30% International',
          riskAlignment: 'Opportunity for Improvement',
          interpretation: `Broadening your geographic exposure reduces risk in your portfolio.`
        },
        {
          analysisType: 'Diversification (Sector)',
          startingValue: 'No significant over/underweights',
          destinationValue: 'No significant over/underweights',
          riskAlignment: 'Similar',
          interpretation: `You are well diversified across sectors, with exposure in line with the broad market.`
        },
      ]
    };
  },
  computed: {
    introText() {
      return "This executive summary compares key aspects of your Current Portfolio with the Proposed Portfolio, interpreting how changes may align with your moderately aggressive risk target based on your risk questionnaire score.";
    },
    footerText() {
      return `Report generated on ${new Date().toLocaleDateString()}. This summary is for informational purposes only and does not constitute investment advice. Interpretations are based on general principles and may not account for all factors affecting your personal financial situation.`;
    },
  },
  methods: {
    getRiskAlignmentClass(alignment) {
      switch (alignment) {
        case 'Opportunity for Improvement':
          return 'tw-text-green-600';
        case 'Similar':
          return 'tw-text-yellow-600';
        default:
          return 'tw-text-gray-600';
      }
    }
  }
};
</script>

<style scoped>
/* it looks like bulma is causing collision wiht default table header colors, or maybe oruga. wherever it is, it is an include in the compiled css and not visible when grepping the codebase */
table th {
  color: white;
}
</style>
